/**
 * One page, as a card inside its BU's section.
 *
 * The active switch writes immediately — the handoff has switches in a listing take effect on
 * toggle, and only switches inside a modal wait for Save. Edit and delete render only when the
 * user holds the flag; a missing flag removes the control rather than disabling it.
 *
 * @module Pages/BUs/Pages
 */

import { css } from '@emotion/css';
import type { JSX } from 'react';
import type { Page } from '../../Types/Pages.types';
import { findCatalogPage } from '@/Shared/brand';
import { IconButton } from '@/Shared/components/IconButton/IconButton';
import { StatusTag } from '@/Shared/components/StatusTag/StatusTag';
import { Switch } from '@/Shared/components/Switch/Switch';
import { Tag } from '@/Shared/components/Tag/Tag';

const cardStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-m);
  padding: var(--space-ml);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-card);
  background: var(--color-surface);
  transition: box-shadow var(--duration-fast) var(--ease-standard);

  &:hover {
    box-shadow: var(--shadow-md);
  }
`;

const tagsRowStyle = css`
  display: flex;
  align-items: center;
  gap: var(--space-s);
  flex-wrap: wrap;
`;

const identityStyle = css`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
`;

const nameStyle = css`
  font-size: 14px;
  font-weight: var(--font-weight-semibold);
`;

const slugStyle = css`
  font-family: var(--font-mono);
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
`;

const footerStyle = css`
  display: flex;
  align-items: center;
  gap: var(--space-s);
  padding-top: var(--space-s);
  border-top: 1px solid var(--color-divider);
  margin-top: auto;
`;

const switchLabelStyle = css`
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
`;

const spacerStyle = css`
  flex: 1;
`;

export interface PageCardProps {
  page: Page;
  canEdit: boolean;
  canDelete: boolean;
  onToggleActive: (page: Page, isActive: boolean) => void;
  onEdit: (page: Page) => void;
  onDelete: (page: Page) => void;
}

export const PageCard = ({
  page,
  canEdit,
  canDelete,
  onToggleActive,
  onEdit,
  onDelete,
}: PageCardProps): JSX.Element => {
  const statusLabel = page.isActive ? 'Ativa' : 'Inativa';

  /**
   * The route the page's slug maps to in its dashboard. The BFF stores a `route` too, but the
   * catalog is the source of truth for where the slug actually resolves.
   */
  const route = findCatalogPage(page.tenantSlug, page.slug)?.route ?? page.route;

  return (
    <article id={`page-card-${page.id}`} className={cardStyle}>
      <div className={tagsRowStyle}>
        <StatusTag status={page.isActive ? 'success' : 'neutral'} size='sm'>
          {statusLabel}
        </StatusTag>
        {page.isMain ? (
          <Tag tone='primary' selected>
            Principal
          </Tag>
        ) : null}
      </div>

      <div className={identityStyle}>
        <span className={nameStyle}>{page.name}</span>
        <span className={slugStyle}>
          {page.slug}
          {route ? ` · ${route}` : ''}
        </span>
      </div>

      <div className={footerStyle}>
        <Switch
          id={`page-active-${page.id}`}
          checked={page.isActive}
          aria-label={`Página ${page.name} ativa`}
          onChange={isActive => {
            onToggleActive(page, isActive);
          }}
        />
        <span className={switchLabelStyle}>{statusLabel}</span>

        <div className={spacerStyle} />

        {canEdit ? (
          <IconButton
            id={`page-edit-${page.id}`}
            icon='Settings.PencilSimple'
            size='sm'
            aria-label={`Editar página ${page.name}`}
            onClick={() => {
              onEdit(page);
            }}
          />
        ) : null}
        {canDelete ? (
          <IconButton
            id={`page-delete-${page.id}`}
            icon='Misc.Trash'
            size='sm'
            aria-label={`Excluir página ${page.name}`}
            onClick={() => {
              onDelete(page);
            }}
          />
        ) : null}
      </div>
    </article>
  );
};
